import React, { useState, useEffect } from 'react';
import Menu from './menu';
import './styles/dashboard.css';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Dashboard = ({ onLogout }) => {
  const [greeting, setGreeting] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [date, setDate] = useState('');
  const [pendingPosts, setPendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const { t, language } = useLanguage();

  useEffect(() => {
    // Indstil hilsen baseret på tidspunkt på dagen
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setGreeting(t('dashboard', 'greeting.morning'));
    } else if (hour >= 12 && hour < 18) {
      setGreeting(t('dashboard', 'greeting.afternoon'));
    } else {
      setGreeting(t('dashboard', 'greeting.evening'));
    }

    // Opdater tid og dato
    updateTimeAndDate();
    
    // Opdater tid hvert minut
    const interval = setInterval(updateTimeAndDate, 60000);
    
    // Tilføj event listener til vindue-størrelse
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, [language, t]);

  useEffect(() => {
    // Hent ventende LinkedIn-opslag, når brugeren er autentificeret
    if (auth.currentUser) {
      fetchPendingPosts();
    }
  }, [auth.currentUser]);

  const updateTimeAndDate = () => {
    const now = new Date();
    
    // Formater tid (HH:MM)
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    setCurrentTime(`${hours}:${minutes}`);
    
    // Formater dato (Ugedag, D. Måned ÅÅÅÅ) baseret på det valgte sprog
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    let localeCode;
    
    switch(language) {
      case 'english':
        localeCode = 'en-US';
        break;
      case 'norwegian':
        localeCode = 'nb-NO';
        break;
      case 'swedish':
        localeCode = 'sv-SE';
        break;
      case 'danish':
      default:
        localeCode = 'da-DK';
    }
    
    setDate(now.toLocaleDateString(localeCode, options));
  };

  const fetchPendingPosts = () => {
    const userId = auth.currentUser.uid;
    
    // Opsæt en real-time listener til webapp_posts collection
    const postsRef = collection(db, 'users', userId, 'webapp_posts');
    const pendingPostsQuery = query(postsRef, where('status', '==', 'pending'));
    
    const unsubscribe = onSnapshot(pendingPostsQuery, (snapshot) => {
      const posts = [];
      snapshot.forEach((doc) => {
        posts.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        });
      });
      
      // Sorter efter oprettelsesdato (nyeste først)
      posts.sort((a, b) => b.createdAt - a.createdAt);
      
      setPendingPosts(posts);
      setLoading(false);
    }, (error) => {
      console.error('Fejl ved hentning af ventende opslag:', error);
      setLoading(false);
    });
    
    // Returner unsubscribe-funktionen, så vi kan rydde op, når komponenten unmounts
    return unsubscribe;
  };

  const formatDate = (date) => {
    if (!date) return '';
    
    // Formater dato baseret på det valgte sprog
    let localeCode;
    
    switch(language) {
      case 'english':
        localeCode = 'en-US';
        break;
      case 'norwegian':
        localeCode = 'nb-NO';
        break;
      case 'swedish':
        localeCode = 'sv-SE';
        break;
      case 'danish':
      default:
        localeCode = 'da-DK';
    }
    
    const options = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
    return date.toLocaleDateString(localeCode, options);
  };
  
  const truncateContent = (content, maxLength = 100) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      
      <main className="dashboard-content">
        <div className="dashboard-header">
          <div className="welcome-section">
            <h1>{greeting}{auth.currentUser?.displayName ? `, ${auth.currentUser.displayName.split(' ')[0]}` : ''}</h1>
            <div className="date-time">
              <span className="time">{currentTime}</span>
              <span className="date">{isMobileView ? date.split(',')[0] : date}</span>
            </div>
          </div>
        </div>
        
        <div className="dashboard-grid simplified">
          <section className="recent-activity">
            <div className="section-header">
              <h2>{t('dashboard', 'recentActivity.title')}</h2>
            </div>
            <div className="activity-empty-state">
              <i className="fas fa-clock"></i>
              <p>{t('dashboard', 'recentActivity.empty')}</p>
            </div>
          </section>
          
          <section className="notifications">
            <div className="section-header">
              <h2>{t('dashboard', 'notifications.title')}</h2>
              {pendingPosts.length > 0 && (
                <span className="dashboard-notification-badge">{pendingPosts.length}</span>
              )}
            </div>
            
            {loading ? (
              <div className="dashboard-notification-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>{t('dashboard', 'notifications.loading')}</p>
              </div>
            ) : pendingPosts.length > 0 ? (
              <div className="dashboard-notification-list">
                {pendingPosts.map(post => (
                  <Link to="/ai-generator" key={post.id} className="dashboard-notification-item linkedin-post">
                    <div className="dashboard-notification-icon">
                      <i className="fab fa-linkedin"></i>
                    </div>
                    <div className="dashboard-notification-content">
                      <h3>{t('dashboard', 'notifications.newPost')}</h3>
                      <p className="dashboard-notification-preview">{truncateContent(post.content, isMobileView ? 60 : 100)}</p>
                      <span className="dashboard-notification-time">{formatDate(post.createdAt)}</span>
                    </div>
                    <div className="dashboard-notification-action">
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  </Link>
                ))}
                
                {pendingPosts.length > 1 && (
                  <div className="dashboard-notification-see-all">
                    <Link to="/ai-generator">{t('dashboard', 'notifications.seeAll', { count: pendingPosts.length })}</Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="notifications-empty-state">
                <i className="fas fa-bell"></i>
                <p>{t('dashboard', 'notifications.empty')}</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
